import { Badge } from '../ui/Badge';
import { Shield, ShieldAlert, ShieldCheck, ShieldOff, ShieldQuestion } from 'lucide-react';
import { cn } from '../../lib/utils';

interface CustodyStatusIndicatorProps {
  status: string | null;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const statusConfig: Record<string, {
  label: string;
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
  icon: typeof Shield;
  color: string;
}> = {
  ACTIVE: {
    label: 'Custódia Ativa',
    variant: 'default',
    icon: ShieldCheck,
    color: 'text-green-600',
  },
  PENDING: {
    label: 'Custódia Pendente',
    variant: 'secondary',
    icon: Shield,
    color: 'text-yellow-600',
  },
  SUSPENDED: {
    label: 'Custódia Suspensa',
    variant: 'destructive',
    icon: ShieldAlert,
    color: 'text-orange-600',
  },
  RELEASED: {
    label: 'Custódia Liberada',
    variant: 'outline',
    icon: ShieldOff,
    color: 'text-gray-600',
  },
};

export function CustodyStatusIndicator({
  status,
  showLabel = true,
  size = 'md',
  className,
}: CustodyStatusIndicatorProps) {
  if (!status) {
    return (
      <Badge variant="outline" className={cn('gap-1', className)}>
        <ShieldQuestion className={cn(
          size === 'sm' ? 'h-3 w-3' : size === 'lg' ? 'h-5 w-5' : 'h-4 w-4',
          'text-gray-400'
        )} />
        {showLabel && <span>Sem Custódia</span>}
      </Badge>
    );
  }

  const config = statusConfig[status] || {
    label: status,
    variant: 'outline' as const,
    icon: ShieldQuestion,
    color: 'text-gray-600',
  };

  const Icon = config.icon;

  return (
    <Badge variant={config.variant} className={cn('gap-1', className)}>
      <Icon className={cn(
        size === 'sm' ? 'h-3 w-3' : size === 'lg' ? 'h-5 w-5' : 'h-4 w-4',
        config.color
      )} />
      {showLabel && <span>{config.label}</span>}
    </Badge>
  );
}

export default CustodyStatusIndicator;
